import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { UserData } from "@/entities/users/types";
import ScoringTableCardController, {
  ScoringTableCardControllerProps
} from "@/components/layouts/ScoringTableCard/ScoringTableCardController";
import HorizBarPercentage from "@/components/controls/HorizBarPercentage";
import CircularPercentage from "@/components/controls/CircularPercentage";
import { displayTotalCurrency, numberSortingFn } from "@/utils/numbers";
import Text from "@/components/ui/text";
import { OverallScoringData, SettingsPeriod } from "@/entities/reporting/types";
import { useThresholds } from "@/entities/settings/hooks";
import { SettingsThresholdsType } from "@/entities/settings/types";
import { QuotaAchievementProvider } from "./QuotaAchievementContext";
import { useArrMrr } from "@/services/context/ArrMrrContext";
import {
  formatPeriodCase,
  periodEnumToString
} from "@/entities/reporting/helpers";

interface Props {
  leadUser: UserData | undefined;
  overallData: OverallScoringData;
  isArr: boolean;
  period?: SettingsPeriod;
  isLoading?: boolean;
}

const QuotaAchievement: React.FC<Props> = ({
  leadUser,
  overallData,
  period,
  isLoading
}) => {
  const { t } = useTranslation();
  const { label: arrMrrLabel } = useArrMrr();

  const dataTestPrefix = "quotaachievement";
  const { thresholds } = useThresholds();
  const quotaAchievementThresholds = useMemo(
    () => thresholds.find((th) => th.id === SettingsThresholdsType.QUOTA_ACHIEVEMENT_LEVELS),
    [thresholds]
  );

  const quotaAchievementProps: ScoringTableCardControllerProps = useMemo(
    () => ({
      dataTestPrefix: dataTestPrefix,
      labelProps: {
        title: t("quotaachievement.title"),
        subtitle: t("quotaachievement.subtitle"),
        empty: {
          title: t("quotaachievement.empty.title"),
          subtitle: t("quotaachievement.empty.subtitle")
        }
      },
      headerDataWidget: (
        <div
          data-test="quotaachievement-overall-score"
          className="min-w-60 w-60 min-h-20 h-20 flex flex-col flex-nowrap justify-start content-stretch align-start mx-1 invisible"
        >
          <div className="flex flex-row justify-end items-center">
            <Text
              as="div"
              variant="smBodySecondary"
              className="w-24 ml-2 mb-0.5"
            >
              <span className="line-clamp-2 text-start">
                {t("quotaachievement.overall.title")}
              </span>
              <Text
                as="div"
                variant="smBodySecondary"
                className="flex flex-row w-28"
              >
                <span className="self-end">
                  {t("quotaachievement.overall.compared")}
                </span>
                {/* Disabled until we get actual values, hidden for the time being */}
                {/* <span
                  className={cn(
                    "flex items-end justify-end size-5 mx-1 mt-3",
                    overallTrendUp
                      ? "text-charts-positive"
                      : "text-charts-negative"
                  )}
                >
                  {overallTrendUp ? <ArrowUp /> : <ArrowDown />}
                </span> */}
              </Text>
            </Text>
            <Text
              as="div"
              variant="lgLabel"
              className="flex flex-row items-center align-center justify-end mx-2"
            >
              <CircularPercentage
                value={overallData?.probability ?? 0}
                useDefaultLevels
                className="min-w-16 min-h-16 max-w-16 max-h-16"
              />
            </Text>
          </div>
        </div>
      ),
      tableDef: {
        simpleColumns: [
          { name: t("quotaachievement.table.name") },
          {
            name: t("quotaachievement.table.directreports"),
            size: 66,
            textAlign: "center"
          },
          {
            name: t("quotaachievement.table.ytd", {
              arrMrr: arrMrrLabel,
              period: period
                ? formatPeriodCase(
                    t(`timeframes.${periodEnumToString(period)}`)
                  )
                : ""
            }),
            size: 80,
            formatter: (value) => displayTotalCurrency(value)
          }
        ],
        summaryColumn: {
          header: t("quotaachievement.table.quotaachievement"),
          accessorKey: "summaryValue",
          size: 150,
          cell: ({ getValue }: { getValue: () => number }) => {
            const percentage = getValue();
            return (
              <HorizBarPercentage
                value={percentage}
                className="min-w-20 h-6"
                levels={quotaAchievementThresholds}
              />
            );
          },
          sortingFn: numberSortingFn,
          meta: {
            textAlign: "start"
          }
        },
        initialSummarySortDesc: false
      },
      isLoading
    }),
    [t, overallData, quotaAchievementThresholds]
  );

  return (
    <QuotaAchievementProvider leadUser={leadUser}>
      <ScoringTableCardController {...quotaAchievementProps} />
    </QuotaAchievementProvider>
  );
};

export default QuotaAchievement;
